// src/components/messaging/NewChatModal.tsx

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveDisplayName } from '@levelup/central-auth';
import { Search, X, Users, MessageSquare } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useGetTeamMembersQuery } from '../../api/teamApi';
import { useCreateChatThreadMutation } from '../../api/uniboxApi';
import type { TeamMemberSummary } from '../../types/api';

interface NewChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string;
  currentUserId?: string;
}

export default function NewChatModal({
  isOpen,
  onClose,
  workspaceId,
  currentUserId,
}: NewChatModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'participants' | 'details'>('participants');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<TeamMemberSummary[]>([]);
  const [chatName, setChatName] = useState('');
  const [initialMessage, setInitialMessage] = useState('');

  const { data: teamMembers = [], isLoading: isLoadingTeam } = useGetTeamMembersQuery(
    { workspaceId },
    { skip: !isOpen }
  );

  const [createChatThread, { isLoading: isCreating }] = useCreateChatThreadMutation();

  // Filter out current user and filter by search query
  const filteredMembers = useMemo(() => {
    return teamMembers.filter((member) => {
      if (member.userId === currentUserId) return false;
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        (member.fullName?.toLowerCase().includes(query)) ||
        member.email.toLowerCase().includes(query)
      );
    });
  }, [teamMembers, currentUserId, searchQuery]);

  const toggleUser = (user: TeamMemberSummary) => {
    setSelectedUsers((prev) => {
      const isSelected = prev.some((u) => u.userId === user.userId);
      if (isSelected) {
        return prev.filter((u) => u.userId !== user.userId);
      }
      return [...prev, user];
    });
  };

  const handleNextStep = () => {
    if (selectedUsers.length > 0) {
      setStep('details');
    }
  };

  const handleBack = () => {
    setStep('participants');
  };

  const handleCreate = async () => {
    if (selectedUsers.length === 0) return;

    try {
      const result = await createChatThread({
        workspaceId,
        name: chatName.trim() || undefined,
        participantUserIds: selectedUsers.map((u) => u.userId),
        initialMessage: initialMessage.trim()
          ? { bodyText: initialMessage.trim() }
          : undefined,
      }).unwrap();

      // Navigate to the new thread
      navigate(`/messages?threadId=${result.id}`);
      handleClose();
    } catch (error) {
      console.error('Failed to create chat thread:', error);
    }
  };

  const handleClose = () => {
    setStep('participants');
    setSearchQuery('');
    setSelectedUsers([]);
    setChatName('');
    setInitialMessage('');
    onClose();
  };

  const renderParticipantsStep = () => (
    <>
      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search team members..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          autoFocus
        />
      </div>

      {/* Selected users chips */}
      {selectedUsers.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {selectedUsers.map((user) => (
            <span
              key={user.userId}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
            >
              {resolveDisplayName({ fullName: user.fullName, email: user.email })}
              <button
                onClick={() => toggleUser(user)}
                className="hover:bg-blue-200 rounded-full p-0.5"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Team members list */}
      <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
        {isLoadingTeam ? (
          <div className="p-4 text-center text-gray-500">Loading team members...</div>
        ) : filteredMembers.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            {searchQuery ? 'No members found' : 'No team members available'}
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {filteredMembers.map((member) => {
              const isSelected = selectedUsers.some((u) => u.userId === member.userId);
              return (
                <li key={member.userId}>
                  <button
                    onClick={() => toggleUser(member)}
                    className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600 font-medium flex-shrink-0">
                      {member.avatarUrl ? (
                        <img
                          src={member.avatarUrl}
                          alt={member.fullName || member.email}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        (member.fullName?.charAt(0) || member.email.charAt(0)).toUpperCase()
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {member.fullName || member.email}
                      </p>
                      <p className="text-sm text-gray-500 truncate">{member.email}</p>
                    </div>

                    {/* Checkbox */}
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                        isSelected
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M10.28 2.72a.75.75 0 010 1.06l-5.5 5.5a.75.75 0 01-1.06 0l-2.5-2.5a.75.75 0 011.06-1.06L4.25 7.69l4.97-4.97a.75.75 0 011.06 0z" />
                        </svg>
                      )}
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );

  const renderDetailsStep = () => (
    <>
      {/* Selected participants summary */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
          <Users className="w-4 h-4" />
          <span>{selectedUsers.length} participant{selectedUsers.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="flex flex-wrap gap-1">
          {selectedUsers.map((user) => (
            <span
              key={user.userId}
              className="text-xs bg-white px-2 py-1 rounded border border-gray-200"
            >
              {resolveDisplayName({ fullName: user.fullName, email: user.email })}
            </span>
          ))}
        </div>
      </div>

      {/* Chat name (optional) */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Chat Name (optional)
        </label>
        <input
          type="text"
          placeholder="e.g., Project Discussion, Team Updates..."
          value={chatName}
          onChange={(e) => setChatName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <p className="mt-1 text-xs text-gray-500">
          Leave empty to use participant names as the chat title
        </p>
      </div>

      {/* Initial message (optional) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          First Message (optional)
        </label>
        <textarea
          placeholder="Type your first message... Use @name to mention someone"
          value={initialMessage}
          onChange={(e) => setInitialMessage(e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
      </div>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 'participants' ? 'New Chat' : 'Chat Details'}
      description={
        step === 'participants'
          ? 'Select team members to start a conversation'
          : 'Customize your chat (optional)'
      }
      size="md"
      footer={
        step === 'participants' ? (
          <>
            <Button variant="ghost" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleNextStep}
              disabled={selectedUsers.length === 0}
            >
              Next
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={handleBack}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={handleCreate}
              isLoading={isCreating}
              disabled={isCreating}
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Start Chat
            </Button>
          </>
        )
      }
    >
      {step === 'participants' ? renderParticipantsStep() : renderDetailsStep()}
    </Modal>
  );
}
